'use client';

import { useEffect, useState } from 'react';
import { ApiError } from '@/lib/apiClient';

/**
 * Cache di lettura minimale per le GET pubbliche del backend.
 *
 * La landing è un export statico: i dati arrivano dal browser, quindi ci serve
 * solo (a) non ripetere la stessa richiesta e (b) ricomparire subito se il dato
 * è già in memoria. Niente react-query: una Map di promesse a livello di modulo
 * basta e non pesa sul bundle.
 */

interface CacheEntry<T> {
  promise: Promise<T>;
  value?: T;
  error?: string;
}

const cache = new Map<string, CacheEntry<unknown>>();

export interface QueryState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

const IDLE: QueryState<never> = { data: null, isLoading: false, error: null };

function entryFor<T>(key: string, fetcher: () => Promise<T>): CacheEntry<T> {
  const existing = cache.get(key) as CacheEntry<T> | undefined;
  if (existing) return existing;

  const entry: CacheEntry<T> = { promise: null as unknown as Promise<T> };
  entry.promise = fetcher().then(
    value => {
      entry.value = value;
      return value;
    },
    error => {
      entry.error = error instanceof ApiError ? error.message : 'Errore di rete';
      // Gli errori non restano in cache: il tentativo successivo riparte pulito
      cache.delete(key);
      throw error;
    }
  );
  cache.set(key, entry);
  return entry;
}

function snapshot<T>(key: string | null): QueryState<T> {
  if (!key) return IDLE;
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  if (entry?.value !== undefined) return { data: entry.value, isLoading: false, error: null };
  return { data: null, isLoading: true, error: null };
}

/**
 * `key` identifica la richiesta (null = nessuna richiesta da fare).
 * `fetcher` dev'essere stabile: una funzione di modulo o un `useCallback`.
 */
export function useCachedQuery<T>(key: string | null, fetcher: () => Promise<T>): QueryState<T> {
  const [state, setState] = useState<QueryState<T>>(() => snapshot<T>(key));
  const [activeKey, setActiveKey] = useState(key);

  // Cambio di chiave: azzeriamo in fase di render, così non si vede mai il
  // dato della richiesta precedente sotto la nuova (pattern React ufficiale).
  if (activeKey !== key) {
    setActiveKey(key);
    setState(snapshot<T>(key));
  }

  useEffect(() => {
    if (!key) return;
    let active = true;
    const entry = entryFor(key, fetcher);
    entry.promise.then(
      value => {
        if (active) setState({ data: value, isLoading: false, error: null });
      },
      () => {
        if (active)
          setState({ data: null, isLoading: false, error: entry.error ?? 'Errore di rete' });
      }
    );
    return () => {
      active = false;
    };
  }, [key, fetcher]);

  return state;
}
